import { SUPABASE_URL } from '../utils/supabase/url';
import { projectId as supabaseProjectId } from '../utils/supabase/info';
import { getAuthHeaders, getAuthHeaderToken } from '../utils/supabase/client';
import type { CoshhDocument, RamsCoshhDocumentAssignment } from '../types/healthSafety';
import { NativeBridge } from './NativeBridge';

export interface ControlledDocumentUploadMetadata {
  title: string;
  revisionCode: string;
  documentType: string;
  scope?: 'workspace' | 'platform';
  productName?: string;
  manufacturer?: string;
  issueDate?: string;
  reviewDueDate?: string;
  sourceUrl?: string;
  notes?: string;
}

const API_BASE = `${SUPABASE_URL}/functions/v1/make-server-a476273b`;

interface NativeOpenFileEventDetail {
  requestId?: string;
  message?: string;
}

function nativeMacOpen(document: CoshhDocument, authToken: string): Promise<void> {
  const requestId = typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `coshh-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('quantis:native:openFileCompleted', onCompleted as EventListener);
      window.removeEventListener('quantis:native:openFileFailed', onFailed as EventListener);
    };
    const matches = (event: Event) =>
      (event as CustomEvent<NativeOpenFileEventDetail>).detail?.requestId === requestId;
    const onCompleted = (event: Event) => {
      if (!matches(event)) return;
      cleanup();
      resolve();
    };
    const onFailed = (event: Event) => {
      if (!matches(event)) return;
      const message = (event as CustomEvent<NativeOpenFileEventDetail>).detail?.message;
      cleanup();
      reject(new Error(message || 'The document could not be opened'));
    };
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error('The document took too long to open. Please try again.'));
    }, 30_000);

    window.addEventListener('quantis:native:openFileCompleted', onCompleted as EventListener);
    window.addEventListener('quantis:native:openFileFailed', onFailed as EventListener);
    NativeBridge.openFile(document.downloadUrl, {
      filename: document.driveFileName || `${document.title}.pdf`,
      mimeType: document.mimeType || 'application/pdf',
      mode: 'default',
      authToken,
      requestId,
    });
  });
}

async function request(url: string, init?: RequestInit): Promise<Response> {
  const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });
  const response = await fetch(url, { ...init, headers: { ...headers, ...(init?.headers ?? {}) } });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `Request failed (${response.status})`);
  }
  return response;
}

export const COSHHDocumentsService = {
  async upload(projectId: string, file: File, metadata: ControlledDocumentUploadMetadata): Promise<CoshhDocument> {
    const form = new FormData();
    form.set('file', file);
    // Blank optional fields are sent empty, as the native upload does, so the route stores null.
    for (const [key, value] of Object.entries(metadata)) if (value !== undefined) form.set(key, value);
    const response = await fetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/hs/coshh-documents`, {
      method: 'POST', headers: await getAuthHeaders(), body: form,
    });
    const body = await response.json() as { document?: CoshhDocument; error?: string };
    if (!response.ok || !body.document) throw new Error(body.error || 'Unable to upload controlled document');
    return body.document;
  },
  async list(projectId: string): Promise<CoshhDocument[]> {
    // The server's complete traversal uses immutable IDs before display sorting.
    // Offset pages sorted by mutable title/type can silently omit moved rows.
    const response = await request(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/hs/coshh-documents`,
    );
    const body = await response.json() as { documents?: CoshhDocument[]; hasMore?: boolean };
    if (!body || !Array.isArray(body.documents) ||
        (body.hasMore !== undefined && body.hasMore !== false))
      throw new Error('The complete controlled document library could not be confirmed.');
    if (body.documents.length > 20000)
      throw new Error('The controlled document library is too large to load safely.');
    const documents: CoshhDocument[] = [];
    const seen = new Set<string>();
    for (const document of body.documents) {
      if (!document || typeof document.id !== 'string' || !document.id)
        throw new Error('The controlled document library could not be confirmed.');
      if (!seen.has(document.id)) { seen.add(document.id); documents.push(document); }
    }
    // Legacy complete responses omit hasMore; explicit partial responses fail.
    return documents;
  },

  async assign(
    projectId: string,
    ramsId: string,
    substanceId: string,
    documentId: string,
  ): Promise<RamsCoshhDocumentAssignment> {
    const response = await request(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/hs/rams/${encodeURIComponent(ramsId)}/coshh-documents`,
      { method: 'POST', body: JSON.stringify({ substanceId, documentId }) },
    );
    const body = await response.json() as { assignment: RamsCoshhDocumentAssignment };
    return body.assignment;
  },

  async remove(projectId: string, ramsId: string, assignmentId: string): Promise<void> {
    await request(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/hs/rams/${encodeURIComponent(ramsId)}/coshh-documents/${encodeURIComponent(assignmentId)}`,
      { method: 'DELETE' },
    );
  },

  async loadPdf(document: CoshhDocument): Promise<Blob> {
    const response = await request(document.downloadUrl, { headers: { Accept: 'application/pdf' } });
    return response.blob();
  },

  async open(document: CoshhDocument): Promise<void> {
    const filename = document.driveFileName || `${document.title}.pdf`;

    // WKWebView cannot hand an authenticated remote URL directly to Preview.
    // The macOS bridge downloads it with this session token, opens a temporary
    // local PDF, and reports completion so the visible loading state is honest.
    if (NativeBridge.isRunningNatively()) {
      const authToken = await getAuthHeaderToken();
      if (NativeBridge.getPlatform() === 'macos') {
        await nativeMacOpen(document, authToken);
        return;
      }
      NativeBridge.openFile(document.downloadUrl, {
        filename,
        mimeType: document.mimeType || 'application/pdf',
        mode: 'default',
        authToken,
      });
      return;
    }

    const blob = await this.loadPdf(document);
    const url = URL.createObjectURL(blob);
    const anchor = window.document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};
